"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { fetchPresetCoverAsFile, randomCover } from "@/lib/image/covers";
import {
  getListProjectsQueryKey,
  useCreateProject,
  useWebImport,
} from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { WebImport, WebImportButton } from "@/components/ui/web-import";
import { useAuth } from "@/features/auth";
import { UsersSelect } from "@/components/ui/users-select";
import { Icon } from "@/components/ui/icon";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import { useCreateProjectModal } from "../../hooks/use-create-modal";
import { CoverUpload } from "../cover-upload";
import { AccessLevelField } from "../access-level-field";
import {
  createProjectFormSchema,
  type CreateProjectFormValues,
} from "@/features/projects/lib/project-form-schema";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface CreateProjectSubmit {
  title: string;
  description: string;
  access_level: "shared" | "private";
  collaborator_ids?: string[];
  coverFile: File | null;
}

interface CreateProjectProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProjectSubmit) => void;
  isSubmitting?: boolean;
}

function CreateProjectFields({
  onWebImport,
  isWebImportLoading,
}: {
  onWebImport: (url: string) => Promise<void>;
  isWebImportLoading: boolean;
}) {
  const { control } = useFormContext<CreateProjectFormValues>();
  const accessLevel = useWatch({ control, name: "accessLevel" });
  const { user } = useAuth();
  const collaboratorExcludeIds = user?.id ? [user.id] : [];

  return (
    <>
      <FormField
        control={control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Project Title</FormLabel>
            <div className="relative">
              <FormControl>
                <Input
                  type="text"
                  variant="project"
                  size="default"
                  placeholder="New Project"
                  maxLength={100}
                  className="pr-[122px]"
                  {...field}
                />
              </FormControl>
              <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 z-10">
                <WebImport
                  onImport={onWebImport}
                  isLoading={isWebImportLoading}
                >
                  <WebImportButton />
                </WebImport>
              </div>
            </div>
            <FormMessage />
            <p className="hidden text-xs text-contrast/40 leading-4 md:block">
              Use a project to group related hiring work, searches, jobs, and
              collaborators in one place.
            </p>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel optional>Description</FormLabel>
            <FormControl>
              <Textarea
                variant="project"
                placeholder="Describe what this project will be hiring for"
                rows={3}
                maxLength={255}
                className="min-h-20 resize-none md:min-h-28"
                {...field}
              />
            </FormControl>
            <p className="hidden text-xs text-contrast/40 leading-4 md:block">
              Add context about the team, department, or hiring goals so
              everyone understands what this project is for.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="accessLevel"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Access Level</FormLabel>
            <AccessLevelField value={field.value} onChange={field.onChange} />
            <FormMessage />
          </FormItem>
        )}
      />

      <AnimatePresence initial={false}>
        {accessLevel === "private" && (
          <motion.div
            key="collaborators"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 500,
              mass: 0.5,
            }}
            style={{ overflow: "hidden" }}
          >
            <FormField
              control={control}
              name="collaboratorIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Collaborators</FormLabel>
                  <FormControl>
                    <UsersSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select collaborators..."
                      excludeIds={collaboratorExcludeIds}
                    />
                  </FormControl>
                  <FormDescription>
                    Search active organization members and add the teammates who
                    should be able to access this private project.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function CreateProject({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}: CreateProjectProps) {
  // Cover state lives outside react-hook-form: it's a File picked locally and
  // uploaded via a separate, project-scoped endpoint after createProject.
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string>("");
  const [coverInitLoading, setCoverInitLoading] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const setCoverFromFile = useCallback((file: File) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setCoverFile(file);
    setCoverPreviewUrl(url);
  }, []);

  const form = useForm<CreateProjectFormValues>({
    resolver: zodResolver(createProjectFormSchema),
    mode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      accessLevel: "shared",
      collaboratorIds: [],
    },
  });
  const {
    handleSubmit,
    reset: resetForm,
    setValue,
    formState: { isValid },
  } = form;

  const submitForm = useCallback(
    (values: CreateProjectFormValues) => {
      if (isSubmitting) return;
      onSubmit({
        title: values.title,
        description: values.description.trim(),
        access_level: values.accessLevel,
        collaborator_ids:
          values.accessLevel === "private" ? values.collaboratorIds : undefined,
        coverFile,
      });
    },
    [isSubmitting, onSubmit, coverFile],
  );

  // Reset form + load a fresh random preset cover whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    resetForm({
      title: "",
      description: "",
      accessLevel: "shared",
      collaboratorIds: [],
    });

    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- bootstrap a preset cover file when the modal opens
    setCoverInitLoading(true);
    setCoverPreviewUrl(randomCover());
    setCoverFile(null);
    void (async () => {
      try {
        const presetUrl = randomCover();
        const file = await fetchPresetCoverAsFile(presetUrl);
        if (cancelled) return;
        setCoverFromFile(file);
      } catch {
        // If the preset fetch fails, the user can still pick another via the
        // Re-Generate / Upload buttons. The previewUrl already holds the raw
        // preset path, so the modal still renders something.
      } finally {
        if (!cancelled) setCoverInitLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, resetForm, setCoverFromFile]);

  // Revoke any blob URLs when the modal closes / unmounts.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const webImportMutation = useWebImport({
    mutation: {
      onSuccess: (result) => {
        setValue("title", result.project_title, {
          shouldValidate: true,
          shouldDirty: true,
        });
        showToast.success("Imported successfully");
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "Failed to import.";
        showToast.error("Import failed", { description: message });
      },
    },
  });

  const handleWebImport = useCallback(
    async (url: string) => {
      await webImportMutation.mutateAsync({ data: { url } });
    },
    [webImportMutation],
  );

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent size="2xl" className="max-h-[90vh]">
        <Form {...form}>
          <form
            onSubmit={handleSubmit(submitForm)}
            className="flex min-h-0 w-full max-h-inherit max-h-[90vh] flex-1 flex-col md:max-h-[90vh] md:flex-row"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-border bg-surface-header px-5 py-4 md:hidden">
              <ModalTitle className="text-lg font-medium text-heading leading-6">
                Create a new project
              </ModalTitle>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close create project"
                className="flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Icon name="IconCrossMedium" size={14} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col md:flex-row">
              <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col bg-surface-header md:w-[475px] md:shrink-0">
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-10 md:py-10">
                    <div className="flex flex-col gap-4">
                      <ModalTitle className="hidden text-2xl font-medium text-heading leading-6 md:block">
                        Create a new project
                      </ModalTitle>
                      <div className="flex flex-col gap-5 md:gap-6">
                        <div className="md:hidden">
                          <CoverUpload
                            previewUrl={coverPreviewUrl}
                            onPickFile={setCoverFromFile}
                            uploading={coverInitLoading}
                            variant="modal-mobile"
                          />
                        </div>

                        <CreateProjectFields
                          onWebImport={handleWebImport}
                          isWebImportLoading={webImportMutation.isPending}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center justify-between border-t border-border px-5 py-4 md:px-10 md:py-6">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <SubmitButton
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={!isValid || isSubmitting || coverInitLoading}
                      loading={isSubmitting}
                      loadingLabel="Creating"
                    >
                      Create Project
                    </SubmitButton>
                  </div>
                </div>
              </div>

              <div className="hidden min-h-0 min-w-0 flex-1 flex-col bg-surface-primary px-10 py-10 md:flex md:border-l md:border-border">
                <div className="flex w-full items-center justify-between">
                  <span
                    id="create-project-cover-heading"
                    className="text-base font-medium text-heading leading-6"
                  >
                    Project Cover
                  </span>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close create project"
                    className="flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    <Icon name="IconCrossMedium" size={14} />
                  </button>
                </div>

                <CoverUpload
                  previewUrl={coverPreviewUrl}
                  onPickFile={setCoverFromFile}
                  uploading={coverInitLoading}
                  variant="modal-desktop"
                />
              </div>
            </div>
          </form>
        </Form>
      </ModalContent>
    </Modal>
  );
}

export function CreateProjectRoot() {
  const queryClient = useQueryClient();
  const { createProjectOpen, closeCreateProject } = useCreateProjectModal();

  const createProject = useCreateProject({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        queryClient.invalidateQueries({ queryKey: ["projects", "navbar"] });
        queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
        showToast.success("Project created");
        closeCreateProject();
      },
      onError: (err) => {
        if (isPermissionDenied(err)) {
          showToast.error("Permission denied", {
            description: getPermissionErrorMessage(err),
          });
        } else {
          showToast.error("Creation failed", {
            description: "Failed to create project.",
          });
        }
      },
    },
  });

  return (
    <CreateProject
      open={createProjectOpen}
      onClose={closeCreateProject}
      onSubmit={(data) => {
        createProject.mutate({
          data: {
            title: data.title,
            description: data.description.trim() || null,
            access_level: data.access_level,
            collaborator_ids: data.collaborator_ids ?? [],
            cover: data.coverFile ?? null,
          },
        });
      }}
      isSubmitting={createProject.isPending}
    />
  );
}
